import SEO from "@/components/SEO";
import { FadeIn, MaskedLines, Chapter } from "@/components/motion";
import CtaSection from "@/components/CtaSection";
import { WHY_INDIA } from "@/data/content";

export default function WhyIndia() {
  return (
    <>
      <SEO
        title="Why International Patients Consider Treatment in India | GetWellSoon"
        description="Experienced specialists, advanced hospital infrastructure, and dedicated international patient departments — a balanced, honest look at medical travel to India."
      />

      <section className="bg-pearl" data-testid="why-india-hero">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <Chapter index="01" label="Why India" />
          <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:items-end">
            <MaskedLines
              delay={0.1}
              className="font-serif text-4xl leading-[1.06] tracking-tight text-ink sm:text-5xl lg:col-span-8 lg:text-6xl"
              lines={[
                "A measured look at",
                <span key="l2">
                  treatment in <span className="italic text-brand">India</span>.
                </span>,
              ]}
            />
            <FadeIn delay={0.4} className="lg:col-span-4">
              <p className="text-base leading-relaxed text-muted-foreground">
                No slogans and no promises. These are the practical reasons
                international patients consider India — presented so you can
                weigh them for yourself.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="why-india-reasons">
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          {WHY_INDIA.map((r, i) => (
            <FadeIn
              key={r.title}
              className="grid gap-6 border-t border-border py-14 md:grid-cols-12 md:gap-10"
              data-testid={`why-reason-${i + 1}`}
            >
              <div className="font-serif text-5xl italic text-sage-light md:col-span-2">
                0{i + 1}
              </div>
              <div className="md:col-span-4">
                <h2 className="font-serif text-2xl tracking-tight text-ink md:text-3xl">
                  {r.title}
                </h2>
                <p className="mt-3 text-sm font-semibold text-sage-dark">
                  {r.short}
                </p>
              </div>
              <div className="md:col-span-6">
                <p className="text-base leading-relaxed text-muted-foreground">
                  {r.detail}
                </p>
              </div>
            </FadeIn>
          ))}

          <div className="grid gap-6 border-t border-border pt-20 lg:grid-cols-12">
            <FadeIn className="lg:col-span-7">
              <div className="overflow-hidden rounded-[2rem] rounded-tr-[5rem] border border-border">
                <img
                  src="/images/mri.jpg"
                  alt="An advanced operating suite at a leading private hospital in India"
                  className="h-[320px] w-full object-cover md:h-[420px]"
                  data-testid="why-india-image-1"
                />
              </div>
            </FadeIn>
            <FadeIn delay={0.15} className="lg:col-span-5 lg:pt-24">
              <div className="overflow-hidden rounded-[2rem] rounded-bl-[4rem] border border-border">
                <img
                  src="/images/doctor-portrait.jpg"
                  alt="An experienced specialist at a hospital in India"
                  className="h-[280px] w-full object-cover md:h-[340px]"
                  data-testid="why-india-image-2"
                />
              </div>
            </FadeIn>
          </div>

          <FadeIn
            className="mt-20 rounded-[2rem] border border-border bg-white p-8 md:p-12"
            data-testid="why-india-perspective"
          >
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand">
              A measured perspective
            </p>
            <p className="mt-5 max-w-3xl font-serif text-xl leading-relaxed tracking-tight text-ink md:text-2xl">
              India is not the right choice for every patient or every
              condition. Suitability depends on your diagnosis, your overall
              health, and the judgment of qualified physicians. Our role is to
              help you evaluate options honestly — including the option of not
              traveling at all.
            </p>
          </FadeIn>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
